<script lang="ts">
	/**
	 * The white wordmark is used in two situations, and they are not the same
	 * thing:
	 *
	 * - `inverse` — an always-dark ground regardless of theme, i.e. the ink
	 *   footer. The caller knows, so it is a prop.
	 * - dark mode — the header sits on `--color-surface`, which is dark only
	 *   when the visitor's theme is. Nothing in Svelte knows that at render
	 *   time on a prerendered page, so it is a CSS swap below.
	 *
	 * The default asset is near-black on transparency, so on either ground it
	 * would be an invisible rectangle. This used to be handled with a
	 * `brightness-0 invert` filter; a supplied white asset is better, because the
	 * filter flattened the green tendril in the bird — the default mark is not
	 * flat black, it carries colour the filter cannot preserve.
	 *
	 * Both marks now read "Thailand". The white one used to be a "Bangkok"
	 * variant, which meant the header quietly changed wordmark with the theme.
	 * If a white asset is ever replaced again, match the default's wordmark and
	 * its ~1.87 aspect ratio: `Logo` sets a height and lets width follow, so a
	 * differently proportioned file silently resizes the header.
	 */
	/*
	 * `w=` because `sizes` alone was not enough. Left to itself the plugin emits
	 * two widths — the original and half of it — and the white mark is a 2400px
	 * master, so the smallest source a browser could pick was 1200px wide: 28 KB
	 * to paint a 90px-tall wordmark, on every page, in the header and again in
	 * the footer. Naming the widths gives it something to choose that is
	 * actually the size of the thing on screen.
	 *
	 * 480 and 240: 240 is the widest this ever renders (see `sizes` below) and
	 * 480 is that at 2×. Both marks get the same pair so the theme swap cannot
	 * change how sharp the logo looks.
	 */
	import logo from '$lib/assets/dutchpassion-th-logo.png?enhanced&w=480;240';
	import logoWhite from '$lib/assets/dutchpassion-thailand-logo-white.png?enhanced&w=480;240';
	import { localizeHref } from '$lib/paraglide/runtime';

	/*
	 * The default steps down to `h-12` on phones. At `h-24` this 778x417 mark is
	 * 179px wide, and the mobile header row — logo, cart, language, burger, all
	 * intrinsically sized inside a `shrink-0` wrapper — came to ~450px against a
	 * 375px viewport, widening the whole document. It returns to `h-30` at `lg`,
	 * where it sits beside the three-row desktop stack.
	 */
	let { small, inverse }: { small?: boolean; inverse?: boolean } = $props();

	/** Shared so the two theme variants cannot drift to different heights. */
	const size = $derived(
		`${small ? 'h-12 sm:h-16 md:h-22' : 'h-12 sm:h-20 md:h-24 lg:h-30'} w-auto`
	);

	/**
	 * A ceiling, not a measurement — and the reason the mark is now a few
	 * kilobytes instead of twenty-six.
	 *
	 * `Logo` sets a height and lets the width follow, so its laid-out width is
	 * never in the markup for the browser to read. Without `sizes`, `srcset`
	 * falls back to assuming the intrinsic width, and the white mark is a
	 * 2400px master: every page header and footer downloaded it at 1200px to
	 * paint it 90px wide.
	 *
	 * 15rem is the widest this ever renders — 120px tall at `lg:h-30` against
	 * the ~1.87 aspect ratio, so 224px — rounded up. One number for every
	 * breakpoint rather than a media-query list, because the largest case is
	 * only 2.5× the smallest and the sources come in coarser steps than that.
	 */
	const sizes = '15rem';
</script>

<a href={localizeHref('/')}>
	<!--
		The link's accessible name. The images below are decorative (`alt=""`)
		because of it — with a real `alt` the theme swap would render two images
		carrying the same name, and a screen reader would announce the wordmark
		twice.
	-->
	<span class="sr-only">Dutch Passion Thailand</span>
	{#if inverse}
		<enhanced:img
			class={`${small ? 'h-16 md:h-20' : 'h-20 md:h-24'} w-auto`}
			src={logoWhite}
			{sizes}
			alt=""
		/>
	{:else}
		<!--
			Theme swap, done in CSS rather than by picking one asset in JS.
			The `dark` class lands on <html> before first paint (see `app.html`),
			so whichever mark is right is the only one ever painted; deciding in
			JS instead would show the black wordmark on the dark header until
			hydration.

			Rendering both costs no extra request: the footer renders the white
			mark with `inverse` on every page anyway, so it is already in the
			cache by the time the header needs it.
		-->
		<enhanced:img class={`${size} dark:hidden`} src={logo} {sizes} alt="" />
		<enhanced:img class={`${size} hidden dark:block`} src={logoWhite} {sizes} alt="" />
	{/if}
</a>
